import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import * as React from "react";

import { useViewingContext } from "@/shared/services";
import {
  WORKSPACE_ACTION_TOPIC,
  WorkspaceActionPayload,
  usePublishToAiAgentThreadPubSubBus,
} from "@/shared/services/AiAgentThreadPubSubBus";

import type { HoverAction } from "../HoverActionToolbar/hoverAction";

/**
 * The "Jump to timestamp" toolbar action for a rendered instant, which moves
 * the open visualizer's playhead to it — so any instant a reader can anchor on
 * is also one they can go look at, not just the topic links inside the
 * visualizer that used to offer it.
 *
 * `undefined` when there is no instant, and when no visualizer is open: with
 * no playhead to move there is nothing for a click to do, and an inert icon on
 * every timestamp in a transcript reads as a broken feature rather than an
 * unavailable one. Availability tracks the same reactive registration
 * {@link useVisualizerClock} reads, so opening or closing a workspace adds and
 * removes the icon without a reload.
 *
 * Callers hand the result straight to {@link hoverActions}, which drops it
 * when it is undefined.
 */
export function useJumpToTimestampAction(
  ns: bigint | undefined,
): HoverAction | undefined {
  const publish = usePublishToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
    WORKSPACE_ACTION_TOPIC,
  );
  const available = useViewingContext().visualizer !== undefined;

  return React.useMemo(() => {
    if (ns === undefined || !available) {
      return undefined;
    }
    return {
      key: "jump",
      icon: <TimerOutlinedIcon />,
      tooltip: "Jump to timestamp",
      onClick: () => {
        publish({ type: "seek_timer", payload: { timestamp: ns } });
      },
    };
  }, [ns, publish, available]);
}
